import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation,
  inject,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { DateTime } from 'luxon';

import { IonicModule } from '@ionic/angular';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { ratingFraction, ratingLabel } from 'src/shared/util/review-rating';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { PropertyReviewsService, GuestReview, GuestReviewRatings } from 'src/shared/services/property-reviews.service';

import { StarRatingComponent, RATING_WORDS } from 'src/shared/components/star-rating/star-rating.component';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// Per-category rating as label + value, precomputed so the template stays
// function-free (CLAUDE.md rule).
interface SubRating {
  label: string;
  value: number;
}

@Component({
  selector: 'review-detail',
  templateUrl: './review-detail.component.html',
  styleUrls: ['./review-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    StarRatingComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ReviewDetailComponent implements OnInit {
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private propertyReviewsService = inject(PropertyReviewsService);
  private demoBlock = inject(DemoBlockService);
  windowService = inject(WindowService);

  // Same order/labels the Reviews tab uses for its sub-rating tooltip.
  private static readonly SUB_RATING_LABELS: [string, string][] = [
    ['cleanliness', 'Cleanliness'],
    ['accuracy', 'Accuracy'],
    ['checkIn', 'Check-in'],
    ['communication', 'Communication'],
    ['location', 'Location'],
    ['value', 'Value'],
  ];

  private static readonly PLATFORM_LABELS: Record<string, string> = {
    airbnb: 'Airbnb',
    vrbo: 'VRBO',
    booking_com: 'Booking.com',
  };

  // Brand-tinted chip per platform; empty string falls back to the neutral chip.
  private static readonly PLATFORM_CHIP_CLASSES: Record<string, string> = {
    airbnb: 'platform-chip--airbnb',
    vrbo: 'platform-chip--vrbo',
    booking_com: 'platform-chip--booking',
  };

  review: GuestReview | null = null;
  channelLabel = '';
  platformChipClass = '';
  ratingLabel = '';
  ratingCaption = '';
  overallRating = 0;
  overallStars = 0;
  subRatings: SubRating[] = [];
  hostResponse = '';
  hasResponded = false;
  respondedLabel = '';
  responseError = '';
  respondAvailable = true;
  respondUnavailableMessage = '';
  submitted = false;
  submitting = false;

  form = this.fb.group({
    response: ['', [Validators.required, Validators.minLength(2)]],
  });

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.review = (params?.review as GuestReview) || null;
    this.rightSidePanelService.setDialogPageTitle('Review');

    if (this.review) {
      this.channelLabel = this.formatPlatform(this.review.platform);
      this.platformChipClass = ReviewDetailComponent.PLATFORM_CHIP_CLASSES[this.review.platform] || '';
      this.overallRating = this.review.overall_rating || 0;
      const scale = this.review.rating_scale || 5;
      // The star widget and its words are 5-point; VRBO / Booking.com score out of 10.
      // Stars go to the nearest half so a 9/10 shows 4.5, not a perfect 5.
      const stars = ratingFraction(this.overallRating, scale) * 5;
      this.overallStars = Math.round(stars * 2) / 2;
      this.ratingLabel = ratingLabel(this.overallRating, scale);
      const word = RATING_WORDS[Math.round(stars)] || '';
      this.ratingCaption = word ? `${this.ratingLabel} · ${word}` : this.ratingLabel;
      this.subRatings = this.buildSubRatings(this.review.ratings);
      // Backends without response_status only tell us via host_response.
      this.hasResponded = this.review.response_status
        ? this.review.response_status === 'responded'
        : !!this.review.host_response;
      this.hostResponse = this.review.host_response || '';
      const respondedAt = this.review.responded_at ? DateTime.fromISO(this.review.responded_at) : null;
      this.respondedLabel = respondedAt?.isValid
        ? `Responded · ${respondedAt.toFormat('MMM d, yyyy')}`
        : 'Responded';
      this.respondAvailable = this.review.can_respond === true;
      this.respondUnavailableMessage = `Responding to ${this.channelLabel} reviews from TIDY isn't available yet. Please reply on ${this.channelLabel} directly.`;
      if (this.respondAvailable && this.review.response_status === 'failed') {
        this.responseError = this.failureMessage(this.review);
        // Reopen with the reply that failed so the host can retry or edit it.
        this.form.patchValue({ response: this.review.failed_response || '' });
      }
    }
    this.cdr.markForCheck();
  }

  private buildSubRatings(ratings: GuestReviewRatings | null | undefined): SubRating[] {
    if (!ratings) {
      return [];
    }
    return ReviewDetailComponent.SUB_RATING_LABELS
      .filter(([key]) => typeof ratings[key] === 'number')
      .map(([key, label]) => ({ label, value: ratings[key] as number }));
  }

  private formatPlatform(platform: string): string {
    return ReviewDetailComponent.PLATFORM_LABELS[platform] ?? (platform || '');
  }

  submitResponse = async () => {
    if (this.demoBlock.block()) { return; }
    this.submitted = true;
    if (this.form.invalid || !this.review || this.submitting) {
      return;
    }
    this.submitting = true;
    this.responseError = '';
    this.cdr.markForCheck();
    try {
      const text = (this.form.value.response || '').trim();
      const result = await this.propertyReviewsService.respondToReview(this.review.id, text);
      // The platform rejecting the reply still answers 2xx, recorded on the
      // review as response_status 'failed' — keep the form open to retry.
      if (result?.response_status === 'failed') {
        this.submitting = false;
        this.responseError = this.failureMessage(result);
        this.cdr.markForCheck();
        return;
      }
      this.util.showSuccess('Response posted to the platform.');
      this.close();
    } catch (err: any) {
      this.submitting = false;
      this.responseError = err?.error?.message || err?.message || 'Failed to post response. Please try again.';
      this.cdr.markForCheck();
    }
  };

  private failureMessage(review: GuestReview): string {
    const failedAt = review.response_failed_at ? DateTime.fromISO(review.response_failed_at) : null;
    const when = failedAt?.isValid ? ` on ${failedAt.toFormat('MMM d, yyyy')}` : '';
    const reason = review.response_error ? `: ${review.response_error}` : '.';
    return `Your last response could not be posted${when}${reason} Please try again.`;
  }

  // A plain history back strands the host when the page was opened directly
  // (no history to pop), so always return to the Reviews tab.
  topNavBack = (): boolean => {
    this.close();
    return false;
  };

  private close() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('revenue?tab=reviews');
    }
  }
}
